import { Component, ElementRef, HostListener, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { IData, Column, Table, Enum, ChangesetView } from "../../shared/data/engineering-data";
import {
  ColumnType,
  MeasurementContext,
  MeasurementContextDescriptions,
  SiUnits,
  UnitType,
  UnitTypeDescriptions,
  UsUnits,
} from 'src/app/shared/data/engineering-enums';
import { firstValueFrom } from 'rxjs';
import { UnitConverterService } from 'src/app/shared/services/unit-converter.service';

interface ImportColumnMapping {
  sourceIndex: number;
  targetName: string;
  unitId: number | null;
}

interface EngineeringFilterChip {
  key: string;
  text: string;
}

interface EngineeringImportReport {
  imported: number;
  ignored: number;
  updated: number;
  added: number;
  failed: Array<{ id: string; error: string }>;
  invalidColumns: string[];
  validColumns: string[];
  skippedColumns: string[];
}

enum EngineeringFilterOperator {
  Equal = '=',
  NotEqual = '!=',
  Contains = 'Contains',
  StartsWith = 'Starts With',
  EndsWith = 'Ends With',
  GreaterThanOrEqual = '>=',
  GreaterThan = '>',
  LessThanOrEqual = '<=',
  LessThan = '<',
  Between = 'Between',
}

interface EngineeringDataFilter {
  id: number;
  columnName: string;
  operator: EngineeringFilterOperator;
  value: any;
  value2: any;
}

interface EngineeringDataSortRule {
  id: number;
  columnKey: string;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'engineering-data',
  templateUrl: './engineering-data.component.html',
  styleUrl: './engineering-data.component.scss',
  standalone: false
})
export class EngineeringDataComponent {
  ColumnType = ColumnType;
  private readonly idFilterColumn = Object.assign(new Column(), { Name: 'DataId', TypeId: ColumnType.Int });
  @ViewChild('importReportDialog') private importReportDialog?: TemplateRef<unknown>;
  @ViewChild('encryptedImportDialog') private encryptedImportDialog?: TemplateRef<unknown>;
  @ViewChild('importColumnMappingDialog') private importColumnMappingDialog?: TemplateRef<unknown>;
  unmatchedImportColumns: Array<{ index: number; label: string }> = [];
  unmatchedDataColumns: Array<{ name: string; label: string }> = [];
  importColumnMappings: ImportColumnMapping[] = [];
  importReport?: EngineeringImportReport;
  @ViewChildren('tableEditorColumnName')
  private tableEditorColumnNameInputs?: QueryList<ElementRef<HTMLInputElement>>;
  readonly columnTypeOptions = [
    { id: ColumnType.String, label: 'String' },
    { id: ColumnType.Int, label: 'Int' },
    { id: ColumnType.Double, label: 'Double' },
    { id: ColumnType.Decimal, label: 'Decimal' },
    { id: ColumnType.DateTime, label: 'DateTime' },
    { id: ColumnType.Boolean, label: 'Boolean' },
    { id: ColumnType.Enum, label: 'Enum' },
  ];
  readonly measurementContextOptions = Array.from(MeasurementContextDescriptions, ([id, label]) => ({ id, label }));
  readonly unitTypeOptions = Array.from(UnitTypeDescriptions, ([id, label]) => ({ id, label }));
  private tableControlsElement?: HTMLElement;
  private tableFooterElement?: HTMLElement;
  private tableScrollElement?: HTMLElement;
  private tableControlsObserver?: ResizeObserver;

  constructor(
    private api: ApiService,
    public dialog: MatDialog,
    private unitConverter: UnitConverterService,
  ) { }

  ngOnInit() {
    this.initializeData();
  }

  mode: string = "tables";
  tables: Table[] = [];
  table: Table;
  selectedTable: Table;
  columns: Column[] = [];
  changesets: ChangesetView[] = [];
  comparableChangesets: ChangesetView[] = [];
  currentChangeset: ChangesetView;
  compareChangeset: ChangesetView;
  private originalRows: IData[] = [];
  private originalByDataId = new Map<number, IData>();
  get originalData(): IData[] {
    return this.originalRows;
  }
  set originalData(rows: IData[]) {
    this.originalRows = rows;
    this.originalByDataId = new Map(rows.map((row) => [row.DataId, row]));
  }
  currentData: IData[] = [];
  enabledFilter: 'all' | 'enabled' | 'disabled' = 'all';
  filterRules: EngineeringDataFilter[] = [];
  sortRules: EngineeringDataSortRule[] = [];
  columnVisibility: Record<string, boolean> = {};
  pageNumber = 1;
  pageSize = 50;
  readonly pageSizeOptions = [10, 25, 50, 100, 200];
  csvMessage = '';
  private nextFilterId = 1;
  private nextSortId = 1;
  private enumRecords: Enum[] = [];
  private enumValuesByType = new Map<string, Enum[]>();
  private enumValuesByCode = new Map<string, Map<number, Enum>>();
  private readonly emptyEnumValues: Enum[] = [];
  get enums(): Enum[] {
    return this.enumRecords;
  }
  set enums(values: Enum[]) {
    this.enumRecords = values;
    this.enumValuesByType = new Map();
    this.enumValuesByCode = new Map();
    for (const value of values) {
      let typeValues = this.enumValuesByType.get(value.Type);
      let codeValues = this.enumValuesByCode.get(value.Type);
      if (!typeValues || !codeValues) {
        typeValues = [];
        codeValues = new Map();
        this.enumValuesByType.set(value.Type, typeValues);
        this.enumValuesByCode.set(value.Type, codeValues);
      }
      typeValues.push(value);
      if (!codeValues.has(value.CodeId)) codeValues.set(value.CodeId, value);
    }
  }
  enumTypes: string[] = [];
  selectedEnumType = '';
  enumItems: Enum[] = [];
  enumColumns: Array<{ tableName: string; column: Column }> = [];
  enumColumnsLoading = false;
  enumValuesLoaded = false;
  enumSaving = false;
  enumMessage = '';
  newEnumTypeName = '';
  selectedTableEditor?: Table;
  tableEditorDraft?: Table;
  tableEditorColumns: Column[] = [];
  tableEditorColumnsLoading = false;
  private originalTableEditorDraft?: Table;
  private originalTableEditorColumns: Column[] = [];
  private tableEditorColumnsRequestId = 0;
  tableEditorSaving = false;
  tableEditorMessage = '';
  private originalEnumItems: Enum[] = [];
  private apiEnumTypes: string[] = [];
  private enumColumnsLoaded = false;
  showOnlyChanges = false;
  showAlternateUnits = false;
  showDisabled: boolean = false;

  @ViewChild('engineeringTableControls')
  set engineeringTableControls(ref: ElementRef<HTMLElement> | undefined) {
    this.tableControlsElement = ref?.nativeElement;
    this.observeTableLayout();
  }

  @ViewChild('engineeringTableFooter')
  set engineeringTableFooter(ref: ElementRef<HTMLElement> | undefined) {
    this.tableFooterElement = ref?.nativeElement;
    this.observeTableLayout();
  }

  @ViewChild('engineeringTableScroll')
  set engineeringTableScroll(ref: ElementRef<HTMLElement> | undefined) {
    this.tableScrollElement = ref?.nativeElement;
    this.updateTableScrollHeight();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateTableScrollHeight();
  }

  ngOnDestroy() {
    this.tableControlsObserver?.disconnect();
  }

  private observeTableLayout() {
    this.tableControlsObserver?.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
      this.tableControlsObserver = new ResizeObserver(() => this.updateTableScrollHeight());
      if (this.tableControlsElement) this.tableControlsObserver.observe(this.tableControlsElement);
      if (this.tableFooterElement) this.tableControlsObserver.observe(this.tableFooterElement);
    }
    this.updateTableScrollHeight();
  }

  private updateTableScrollHeight() {
    if (!this.tableScrollElement) return;
    const top = this.tableScrollElement.getBoundingClientRect().top;
    const footerHeight = this.tableFooterElement?.getBoundingClientRect().height ?? 0;
    this.tableScrollElement.style.height = `${Math.max(120, window.innerHeight - top - footerHeight)}px`;
  }

  get pageStatusMessage(): string {
    if (this.mode === 'enums') {
      if (this.enumColumnsLoading) return 'Loading enum columns...';
      if (!this.enumValuesLoaded) return 'Loading enums...';
      return this.enumMessage;
    }
    if (this.mode === 'tableEditor') return this.tableEditorMessage;
    return this.csvMessage;
  }

  get pageStatusIsError(): boolean {
    const message = this.mode === 'enums'
      ? this.enumMessage
      : this.mode === 'tableEditor' ? this.tableEditorMessage : this.csvMessage;
    return /^(Unable to|Unsupported view|The view references|CSV must include|Enum .* already exists)|skipped \d+ invalid/i.test(message);
  }

  onTableEditorTableChange(table: Table) {
    const requestId = ++this.tableEditorColumnsRequestId;
    this.selectedTableEditor = table;
    this.tableEditorDraft = structuredClone(table);
    this.originalTableEditorDraft = structuredClone(table);
    this.tableEditorColumns = [];
    this.originalTableEditorColumns = [];
    this.tableEditorColumnsLoading = table.Id !== 0;
    this.tableEditorMessage = this.tableEditorColumnsLoading ? 'Loading table columns...' : '';
    if (table.Id === 0) return;

    this.api.EngineeringData.GetTableColumns(table.Id).subscribe({
      next: (columns) => {
        if (requestId !== this.tableEditorColumnsRequestId) return;
        this.tableEditorColumns = structuredClone(columns);
        this.originalTableEditorColumns = structuredClone(columns);
        this.tableEditorColumnsLoading = false;
        this.tableEditorMessage = '';
      },
      error: () => {
        if (requestId !== this.tableEditorColumnsRequestId) return;
        this.tableEditorColumnsLoading = false;
        this.tableEditorMessage = `Unable to load columns for ${table.Name}.`;
      },
    });
  }

  onTableEditorContextChange(column: Column, contextId: number | null) {
    column.ContextId = contextId as number;
    column.UnitId = (contextId === null
      ? null
      : UsUnits.get(contextId as MeasurementContext) ?? null) as number;
  }

  getUnitOptionsForContext(contextId: number | null): Array<{ id: UnitType; label: string }> {
    if (contextId === null || contextId === undefined) return this.unitTypeOptions;

    const measurementContext = contextId as MeasurementContext;
    const validUnitIds = new Set([
      UsUnits.get(measurementContext),
      SiUnits.get(measurementContext),
    ].filter((unitId): unitId is UnitType => unitId !== undefined));
    return this.unitTypeOptions.filter((option) => validUnitIds.has(option.id));
  }

  addTableEditorColumn() {
    const column = new Column();
    column.TableId = this.tableEditorDraft?.Id ?? 0;
    column.TypeId = ColumnType.String;
    column.ContextId = null as unknown as number;
    column.UnitId = null as unknown as number;
    column.DecimalDigits = null as unknown as number;
    this.tableEditorColumns = [...this.tableEditorColumns, column];
    requestAnimationFrame(() => this.tableEditorColumnNameInputs?.last?.nativeElement.focus());
  }

  removeTableEditorColumn(index: number) {
    this.tableEditorColumns = this.tableEditorColumns.filter((_, columnIndex) => columnIndex !== index);
  }

  addNewEditorTable() {
    this.tableEditorColumnsRequestId++;
    this.tableEditorColumnsLoading = false;
    this.tableEditorColumns = [];
    this.originalTableEditorColumns = [];
    const draft = new Table();
    this.selectedTableEditor = draft;
    this.tableEditorDraft = draft;
    this.originalTableEditorDraft = structuredClone(draft);
    this.tableEditorMessage = '';
  }

  resetTableEditor() {
    if (!this.originalTableEditorDraft) return;
    this.tableEditorDraft = structuredClone(this.originalTableEditorDraft);
    this.tableEditorColumns = structuredClone(this.originalTableEditorColumns);
    if (this.tableEditorDraft.Id === 0) this.selectedTableEditor = this.tableEditorDraft;
    this.tableEditorMessage = '';
  }

  saveTableEditor() {
    const draft = this.tableEditorDraft;
    if (!draft || !draft.Name.trim() || this.tableEditorSaving) return;

    this.tableEditorSaving = true;
    const saveRequest = draft.Id === 0
      ? this.api.EngineeringData.CreateEngineeringTable(draft)
      : this.api.EngineeringData.UpdateEngineeringTable(draft);

    saveRequest.subscribe({
      next: (savedTable) => {
        const table = savedTable ?? structuredClone(draft);
        const columns = this.tableEditorColumns.map((column) => ({
          ...column,
          TableId: table.Id,
        }));
        this.api.EngineeringData.UpdateEngineeringTableColumns(table.Id, columns).subscribe({
          next: () => this.finishTableEditorSave(table, columns),
          error: () => {
            this.tableEditorMessage = `Unable to save columns for ${table.Name}.`;
            this.tableEditorSaving = false;
          },
        });
      },
      error: () => {
        this.tableEditorMessage = `Unable to save table ${draft.Name}.`;
        this.tableEditorSaving = false;
      },
    });
  }

  private finishTableEditorSave(table: Table, columns: Column[]) {
    this.tables = [
      ...this.tables.filter((item) => item.Id !== table.Id && item.Name !== table.Name),
      table,
    ].sort((left, right) => left.Name.localeCompare(right.Name, undefined, { sensitivity: 'base' }));
    this.tableEditorDraft = structuredClone(table);
    this.selectedTableEditor = table;
    this.originalTableEditorDraft = structuredClone(table);
    this.tableEditorColumns = structuredClone(columns);
    this.originalTableEditorColumns = structuredClone(columns);
    this.tableEditorMessage = `Saved table ${table.Name}.`;
    this.tableEditorSaving = false;
  }

  onTableChange(table: Table) {
    if (table?.Id !== this.table?.Id && !this.confirmDiscardPendingChanges()) {
      this.selectedTable = this.table;
      return;
    }
    this.table = table;
    this.selectedTable = table;
    this.enabledFilter = 'all';
    this.filterRules = [];
    this.sortRules = [];
    this.pageNumber = 1;
    this.columnVisibility = {};
    this.nextFilterId = 1;
    this.nextSortId = 1;
    this.showOnlyChanges = false;
    this.loadTableData();
  }

  private confirmDiscardPendingChanges(): boolean {
    if (this.pendingRowCount === 0) return true;
    const changeLabel = this.pendingRowCount === 1 ? 'change' : 'changes';
    return window.confirm(`Discard ${this.pendingRowCount} unsaved row ${changeLabel} and switch tables?`);
  }

  onModeChange(mode: string) {
    if (mode !== 'enums') return;
    if (!this.selectedEnumType && this.enumTypes.length) {
      this.onEnumTypeChange(this.enumTypes[0]);
    }
    void this.loadEnumColumns();
  }

  onEnumTypeChange(type: string) {
    this.selectedEnumType = type;
    this.enumItems = structuredClone(this.enums.filter((item) => item.Type === type));
    this.originalEnumItems = structuredClone(this.enumItems);
    this.enumMessage = '';
  }

  private refreshEnumTypes() {
    this.enumTypes = [...new Set([...this.apiEnumTypes, ...this.enums.map((item) => item.Type)])].sort(
      (left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }),
    );
    if (!this.selectedEnumType || !this.enumTypes.includes(this.selectedEnumType)) {
      this.onEnumTypeChange(this.enumTypes[0] ?? '');
    }
  }

  get selectedEnumColumns(): Array<{ tableName: string; column: Column }> {
    return this.enumColumns.filter((reference) => reference.column.EnumType === this.selectedEnumType);
  }

  addEnumItem() {
    if (!this.selectedEnumType) return;
    const nextCodeId = Math.max(0, ...this.enumItems.map((item) => Number(item.CodeId) || 0)) + 1;
    this.enumItems = [
      ...this.enumItems,
      {
        Id: 0,
        CodeId: nextCodeId,
        Type: this.selectedEnumType,
        Name: '',
        Label: '',
        Abbreviation: '',
        Description: '',
        IsInTools: false,
        ToolsNamespace: '',
      },
    ];
  }

  removeEnumItem(index: number) {
    this.enumItems = this.enumItems.filter((_, itemIndex) => itemIndex !== index);
  }

  resetCurrentEnum() {
    this.enumItems = structuredClone(this.originalEnumItems);
    this.enumMessage = '';
  }

  saveCurrentEnum() {
    if (!this.selectedEnumType || this.enumSaving) return;
    const itemsToSave = structuredClone(this.enumItems).map((item) => ({
      ...item,
      Type: this.selectedEnumType,
    }));
    this.enumSaving = true;
    this.api.EngineeringData.UpdateEngineeringEnums(this.selectedEnumType, itemsToSave).subscribe({
      next: () => {
        this.enums = [
          ...this.enums.filter((item) => item.Type !== this.selectedEnumType),
          ...itemsToSave,
        ];
        this.enumItems = structuredClone(itemsToSave);
        this.originalEnumItems = structuredClone(itemsToSave);
        this.enumMessage = `Saved enum ${this.selectedEnumType}.`;
        this.enumSaving = false;
      },
      error: () => {
        this.enumMessage = `Unable to save enum ${this.selectedEnumType}.`;
        this.enumSaving = false;
      },
    });
  }

  openNewEnumDialog(template: TemplateRef<unknown>) {
    this.newEnumTypeName = '';
    this.dialog.open(template, {
      width: '420px',
      maxWidth: '95vw',
    });
  }

  createEnumType() {
    const type = this.newEnumTypeName.trim();
    if (!type || this.enumSaving) return;
    if (this.enumTypes.some((existingType) => existingType.toLowerCase() === type.toLowerCase())) {
      this.enumMessage = `Enum ${type} already exists.`;
      return;
    }

    this.enumSaving = true;
    this.api.EngineeringData.CreateEngineeringEnumType(type).subscribe({
      next: () => {
        this.apiEnumTypes = [...this.apiEnumTypes, type];
        this.refreshEnumTypes();
        this.onEnumTypeChange(type);
        this.enumMessage = `Created enum ${type}.`;
        this.enumSaving = false;
        this.dialog.closeAll();
      },
      error: () => {
        this.enumMessage = `Unable to create enum ${type}.`;
        this.enumSaving = false;
      },
    });
  }

  private async loadEnumColumns(): Promise<void> {
    if (this.enumColumnsLoaded || this.enumColumnsLoading || !this.tables.length) return;
    this.enumColumnsLoading = true;
    try {
      const tableColumns = await Promise.all(this.tables.map(async (table) => ({
        tableName: this.variableToDisplay(table.Name),
        columns: await firstValueFrom(this.api.EngineeringData.GetTableColumns(table.Id)),
      })));
      this.enumColumns = tableColumns.flatMap(({ tableName, columns }) =>
        columns
          .filter((column) => column.TypeId === ColumnType.Enum)
          .map((column) => ({ tableName, column })),
      );
      this.enumColumnsLoaded = true;
    } catch {
      this.enumMessage = 'Unable to load enum column references.';
    } finally {
      this.enumColumnsLoading = false;
    }
  }

  openFilterPopup(template: TemplateRef<unknown>) {
    this.dialog.open(template, {
      width: '680px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    });
  }

  openColumnsPopup(template: TemplateRef<unknown>) {
    this.dialog.open(template, {
      width: '420px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    });
  }

  clearFilters() {
    this.enabledFilter = 'all';
    this.filterRules = [];
    this.showOnlyChanges = false;
    this.pageNumber = 1;
  }

  clearSorts() {
    this.sortRules = [];
    this.pageNumber = 1;
  }

  resetView() {
    this.clearFilters();
    this.clearSorts();
    this.selectAllColumns();
    this.pageNumber = 1;
    this.csvMessage = '';
  }

  get filteredData(): IData[] {
    const filters = this.filterRules.filter((filter) => this.isFilterComplete(filter));
    return this.currentData.filter((row) => {
      if (this.isDataNew(row)) return true;
      if (this.enabledFilter === 'enabled' && !row.IsEnabled) return false;
      if (this.enabledFilter === 'disabled' && row.IsEnabled) return false;
      if (this.showOnlyChanges && !this.rowHasChanges(row, this.originalByDataId)) return false;
      return filters.every((filter) => this.matchesFilter(row, filter));
    });
  }

  private rowHasChanges(row: IData, originalByDataId: Map<number, IData>): boolean {
    const originalRow = originalByDataId.get(row.DataId);
    if (!originalRow) return true;
    if (row.IsEnabled !== originalRow.IsEnabled || row.SortOrder !== originalRow.SortOrder) return true;
    return this.columns.some((column) => (row as any)[column.Name] != (originalRow as any)[column.Name]);
  }

  get visibleColumns(): Column[] {
    return this.columns.filter((column) => this.columnVisibility[column.Name] !== false);
  }

  get addedRowCount(): number {
    return this.currentData.filter((row) => this.isDataNew(row)).length;
  }

  get changedRowCount(): number {
    return this.currentData.filter((row) =>
      !this.isDataNew(row)
      && this.rowHasChanges(row, this.originalByDataId),
    ).length;
  }

  get pendingRowCount(): number {
    let count = 0;
    for (const row of this.currentData) {
      if (this.rowHasChanges(row, this.originalByDataId)) count++;
    }
    return count;
  }

  get sortColumnOptions(): Array<{ key: string; label: string }> {
    const options = [
      { key: 'DataId', label: 'ID' },
      { key: 'IsEnabled', label: 'Enabled' },
      { key: 'SortOrder', label: 'Order' },
    ];
    const reservedKeys = new Set(options.map((option) => option.key.toLowerCase()));
    return [
      ...options,
      ...this.columns
        .filter((column) => !reservedKeys.has(column.Name.toLowerCase()))
        .map((column) => ({ key: column.Name, label: this.getDisplayHeading(column) })),
    ];
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.filteredData.length / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, index) => index + 1);
  }

  get pagedData(): IData[] {
    const start = (this.pageNumber - 1) * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get sortedData(): IData[] {
    const rows = this.filteredData;
    if (!this.sortRules.length) return rows;
    return rows.sort((left, right) => {
      for (const rule of this.sortRules) {
        const comparison = this.compareSortValues(left, right, rule.columnKey);
        if (comparison !== 0) return rule.direction === 'asc' ? comparison : -comparison;
      }
      return 0;
    });
  }

  private compareSortValues(left: IData, right: IData, columnKey: string): number {
    const leftValue = (left as any)[columnKey];
    const rightValue = (right as any)[columnKey];
    if (leftValue == null || rightValue == null) {
      if (leftValue == null && rightValue == null) return 0;
      return leftValue == null ? -1 : 1;
    }

    if (columnKey === 'DataId' || columnKey === 'SortOrder') {
      return Number(leftValue) - Number(rightValue);
    }
    if (columnKey === 'IsEnabled') {
      return Number(Boolean(leftValue)) - Number(Boolean(rightValue));
    }

    const column = this.columns.find((item) => item.Name === columnKey);
    if (column?.TypeId === ColumnType.Int || column?.TypeId === ColumnType.Double || column?.TypeId === ColumnType.Decimal) {
      return Number(leftValue) - Number(rightValue);
    }
    if (column?.TypeId === ColumnType.DateTime) {
      return new Date(leftValue).getTime() - new Date(rightValue).getTime();
    }
    if (column?.TypeId === ColumnType.Enum) {
      const values = this.enumValuesByCode.get(column.EnumType);
      const leftLabel = values?.get(Number(leftValue))?.Name ?? String(leftValue);
      const rightLabel = values?.get(Number(rightValue))?.Name ?? String(rightValue);
      return leftLabel.localeCompare(rightLabel, undefined, { numeric: true, sensitivity: 'base' });
    }
    return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
  }

  filtersChanged() {
    this.pageNumber = 1;
  }

  onShowOnlyChangesChange(value: boolean) {
    this.showOnlyChanges = value;
    this.pageNumber = 1;
  }

  setPageNumber(page: number) {
    this.pageNumber = Math.min(Math.max(1, Number(page)), this.pageCount);
  }

  setPageSize(pageSize: number) {
    this.pageSize = Number(pageSize);
    this.pageNumber = 1;
  }

  selectAllColumns() {
    this.columnVisibility = Object.fromEntries(this.columns.map((column) => [column.Name, true]));
  }

  deselectAllColumns() {
    this.columnVisibility = Object.fromEntries(this.columns.map((column) => [column.Name, false]));
  }

  isColumnVisible(column: Column): boolean {
    return this.columnVisibility[column.Name] !== false;
  }

  exportCsv() {
    const reservedHeaders = new Set(['id', 'dataid', 'order', 'sortorder', 'enabled', 'isenabled']);
    const exportColumns = this.visibleColumns.filter(
      (column) => !reservedHeaders.has(column.Name.toLowerCase()),
    );
    const headers = ['ID', 'Order', 'Enabled', ...exportColumns.map((column) => column.Name)];
    const records = [
      headers,
      ...this.sortedData.map((row) => [
        String(row.DataId),
        String(row.SortOrder),
        String(row.IsEnabled),
        ...exportColumns.map((column) => this.getCsvColumnValue(row, column)),
      ]),
    ];
    const csv = '\uFEFF' + records.map((record) => record.map((value) => this.escapeCsvValue(value)).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    const filename = (this.table?.Name || 'engineering-data').replace(/[^a-z0-9_-]/gi, '_');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    this.csvMessage = `Exported ${this.sortedData.length} filtered records.`;
  }

  saveView() {
    const view = {
      version: 1,
      tableId: this.table?.Id,
      tableName: this.table?.Name,
      filters: {
        enabled: this.enabledFilter,
        rules: this.filterRules,
        showOnlyChanges: this.showOnlyChanges,
      },
      sorts: this.sortRules.map(({ columnKey, direction }) => ({ columnKey, direction })),
      visibleColumns: this.visibleColumns.map((column) => column.Name),
    };
    const blob = new Blob([JSON.stringify(view, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename = (this.table?.Name || 'engineering-data').replace(/[^a-z0-9_-]/gi, '_');
    link.href = url;
    link.download = `${filename}-view.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async loadView(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const view = JSON.parse(await file.text());
      if (view.version !== 1) throw new Error('Unsupported view version.');
      const targetTable = this.tables.find((candidate) => candidate.Id === Number(view.tableId));
      if (!targetTable) throw new Error('The view references an unavailable table.');
      if (targetTable.Id !== this.table?.Id && !this.confirmDiscardPendingChanges()) return;

      this.csvMessage = '';
      await this.loadTableDataForView(targetTable);

      const validFilters: EngineeringDataFilter[] = [];
      this.nextFilterId = 1;
      if (Array.isArray(view.filters?.rules)) {
        for (const rule of view.filters.rules) {
          const column = this.getColumnByName(rule?.columnName);
          const allowedOperators = column ? this.operatorsForColumn(column) : [];
          if (!column || !allowedOperators.includes(rule.operator)) continue;
          validFilters.push({
            id: this.nextFilterId++,
            columnName: rule.columnName,
            operator: rule.operator,
            value: rule.value ?? null,
            value2: rule.value2 ?? null,
          });
        }
      }

      const validSortKeys = new Set(this.sortColumnOptions.map((option) => option.key));
  this.nextSortId = 1;
      const validSorts: EngineeringDataSortRule[] = Array.isArray(view.sorts)
        ? view.sorts
            .filter((rule: any) => validSortKeys.has(rule?.columnKey)
              && (rule.direction === 'asc' || rule.direction === 'desc'))
            .map((rule: any) => ({
              id: this.nextSortId++,
              columnKey: rule.columnKey,
              direction: rule.direction,
            }))
        : [];

      this.enabledFilter = ['all', 'enabled', 'disabled'].includes(view.filters?.enabled)
        ? view.filters.enabled
        : 'all';
      this.showOnlyChanges = view.filters?.showOnlyChanges === true;
      this.filterRules = validFilters;
      this.sortRules = validSorts;
      const visibleColumnNames = Array.isArray(view.visibleColumns) ? new Set(view.visibleColumns) : null;
      this.columnVisibility = visibleColumnNames
        ? Object.fromEntries(this.columns.map((column) => [column.Name, visibleColumnNames.has(column.Name)]))
        : Object.fromEntries(this.columns.map((column) => [column.Name, true]));
      this.pageNumber = 1;
      this.csvMessage = `Loaded view for ${targetTable.Name}.`;
    } catch (error) {
      if (error instanceof Error && error.message === 'The view references an unavailable table.') {
        this.csvMessage = error.message;
      } else if (error instanceof Error && error.message === 'Unsupported view version.') {
        this.csvMessage = error.message;
      } else {
      this.csvMessage = 'Unable to load this view. Check that the JSON is valid and matches the current table.';
      }
    } finally {
      input.value = '';
    }
  }

  private async loadTableDataForView(table: Table): Promise<void> {
    this.table = table;
    this.selectedTable = table;
    this.originalData = [];
    this.currentData = [];
    this.columns = [];
    this.changesets = [];

    const tableId = table.Id;
    const columns = await firstValueFrom(this.api.EngineeringData.GetTableColumns(tableId));
    const changesets = await firstValueFrom(this.api.EngineeringData.GetChangesets(tableId));
    if (this.table?.Id !== tableId) return;

    this.columns = columns;
    this.changesets = this.comparableChangesets = changesets;
    this.selectAllColumns();
    this.currentChangeset = changesets[changesets.length - 1];
    this.compareChangeset = this.currentChangeset;
    if (!this.currentChangeset) return;

    const data = await firstValueFrom(
      this.api.EngineeringData.GetTableData(tableId, this.currentChangeset.TableChangesetId),
    );
    if (this.table?.Id !== tableId) return;
    this.originalData = data;
    this.currentData = structuredClone(data);
  }

  private getCsvColumnValue(row: IData, column: Column): string {
    const value = (row as any)[column.Name];
    if (value === undefined || value === null) return '';
    if (column.TypeId === ColumnType.Enum) {
      return this.getColumnEnumValues(column).find((option) => option.CodeId === Number(value))?.Name
        ?? String(value);
    }
    return String(value);
  }

  async importCsv(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.csvMessage = '';
    try {
      let records: string[][];
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!worksheet) throw new Error('No worksheet found.');
        records = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
          header: 1,
          defval: '',
          raw: true,
        }).map((record) => record.map((value) =>
          value instanceof Date ? value.toISOString() : String(value ?? ''),
        ));
      } else {
        records = this.parseCsv(await file.text());
      }
      const tableId = this.table?.Id;
      const columnMapping = await this.matchImportColumns(records[0] ?? []);
      if (!columnMapping) return;
      if (this.table?.Id !== tableId) throw new Error('The selected table changed during import. Please import again.');
      this.importReport = this.applyImportRecords(records, columnMapping);
      this.showOnlyChanges = true;
      this.pageNumber = 1;
      this.csvMessage = `Imported ${this.importReport.imported} rows: ${this.importReport.updated} updated, ${this.importReport.added} added, ${this.importReport.ignored} ignored, ${this.importReport.failed.length} failed.`;
      if (this.importReportDialog) {
        this.dialog.open(this.importReportDialog, { width: '720px', maxWidth: '95vw', maxHeight: '90vh' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
      this.csvMessage = `Unable to read the CSV or XLSX file.${message ? ` ${message}` : ''}`;
      if (message.includes('ECMA-376 Encrypted file missing /EncryptionInfo') && this.encryptedImportDialog) {
        this.dialog.open(this.encryptedImportDialog, { width: '520px', maxWidth: '95vw' });
      }
    } finally {
      input.value = '';
    }
  }

  private escapeCsvValue(value: unknown): string {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  private normalizeImportHeader(header: string): string {
    return header.replace(/\([^)]*\)/g, '').replace(/[\s_()\uFEFF]/g, '').toLowerCase();
  }

  private getImportColumnAliases(): Map<string, { name: string; column?: Column }> {
    const aliases = new Map<string, { name: string; column?: Column }>();
    for (const [name, names] of [
      ['DataId', ['id', 'dataid']],
      ['SortOrder', ['order', 'sortorder']],
      ['IsEnabled', ['enabled', 'isenabled']],
    ] as Array<[string, string[]]>) {
      for (const alias of names) aliases.set(alias, { name });
    }
    for (const column of this.columns) {
      const name = column.Name.toLowerCase();
      if (aliases.has(name)) continue;
      const field = { name: column.Name, column };
      aliases.set(name, field);
      const heading = this.normalizeImportHeader(this.getDisplayHeading(column));
      if (!aliases.has(heading)) aliases.set(heading, field);
    }
    return aliases;
  }

  private async matchImportColumns(headers: string[]): Promise<Map<number, string> | null> {
    const aliases = this.getImportColumnAliases();
    const mapping = new Map<number, string>();
    const matched = new Set<string>();
    this.unmatchedImportColumns = [];
    headers.forEach((header, index) => {
      this.unmatchedImportColumns.push({ index, label: header || `Blank header (column ${index + 1})` });
      const field = aliases.get(this.normalizeImportHeader(header));
      if (field && !matched.has(field.name)) {
        mapping.set(index, field.name);
        matched.add(field.name);
      }
    });
    this.unmatchedDataColumns = [...new Map([...aliases.values()].map((field) => [field.name, {
      name: field.name,
      label: field.column ? this.getDisplayHeading(field.column) : field.name === 'DataId' ? 'ID' : field.name === 'SortOrder' ? 'Order' : 'Enabled',
    }])).values()];
    {
      if (!this.importColumnMappingDialog) throw new Error('Column mapping dialog is unavailable.');
      this.importColumnMappings = this.unmatchedImportColumns.map((column) => {
        const pair: ImportColumnMapping = { sourceIndex: column.index, targetName: mapping.get(column.index) ?? '', unitId: null };
        this.onImportMappingTargetChange(pair);
        return pair;
      });
      const dialog = this.dialog.open(this.importColumnMappingDialog, {
        width: '720px', maxWidth: '95vw', maxHeight: '90vh', disableClose: true,
      });
      if (await firstValueFrom(dialog.afterClosed()) !== true) return null;
      mapping.clear();
      for (const pair of this.importColumnMappings) {
        if (pair.targetName) mapping.set(pair.sourceIndex, pair.targetName);
      }
    }
    return mapping;
  }

  getImportMeasurementColumn(pair: ImportColumnMapping): Column | undefined {
    const column = this.columns.find((column) => column.Name === pair.targetName);
    return column?.TypeId === ColumnType.Double && column.ContextId != null ? column : undefined;
  }

  onImportMappingTargetChange(pair: ImportColumnMapping) {
    const column = this.getImportMeasurementColumn(pair);
    pair.unitId = column?.UnitId ?? null;
    if (!column) return;

    const header = this.unmatchedImportColumns.find((source) => source.index === pair.sourceIndex)?.label ?? '';
    const unitLabels: string[] = [];
    const unitPattern = /\(([^()]*)\)/g;
    let unitMatch: RegExpExecArray | null;
    while ((unitMatch = unitPattern.exec(header)) !== null) {
      unitLabels.push(unitMatch[1].trim().toLowerCase());
    }
    const options = this.getUnitOptionsForContext(column.ContextId);
    const match = options.find((option) => unitLabels.some((label) =>
      label === UnitType[option.id].toLowerCase() || label === option.label.toLowerCase(),
    )) ?? options.find((option) => unitLabels.some((label) =>
      label === option.label.toLowerCase().replace(/[\u00b2\u00b3]/g, ''),
    ));
    if (match) pair.unitId = match.id;
  }

  getAvailableImportDataColumns(pair: ImportColumnMapping): Array<{ name: string; label: string }> {
    const selected = new Set(this.importColumnMappings
      .filter((mapping) => mapping !== pair && mapping.targetName)
      .map((mapping) => mapping.targetName));
    return this.unmatchedDataColumns.filter((column) => column.name === pair.targetName || !selected.has(column.name));
  }

  get importMappingsValid(): boolean {
    const mappings = this.importColumnMappings.filter((pair) => pair.targetName);
    return new Set(mappings.map((pair) => pair.sourceIndex)).size === mappings.length
      && new Set(mappings.map((pair) => pair.targetName)).size === mappings.length;
  }

  private applyImportRecords(records: string[][], columnMapping?: Map<number, string>): EngineeringImportReport {
    const report: EngineeringImportReport = {
      imported: 0, ignored: 0, updated: 0, added: 0,
      failed: [], invalidColumns: [], validColumns: [], skippedColumns: [],
    };
    const headers = records.shift() ?? [];
    const aliases = this.getImportColumnAliases();
    const fields: Array<{ name: string; column?: Column; index: number }> = [];
    const seen = new Set<string>();
    headers.forEach((header, index) => {
      const label = header.trim().replace(/^\uFEFF/, '');
      const field = columnMapping
        ? aliases.get(columnMapping.get(index)?.toLowerCase() ?? '')
        : aliases.get(this.normalizeImportHeader(label));
      if (!field) {
        report.invalidColumns.push(label || `Blank header (column ${index + 1})`);
      } else if (seen.has(field.name)) {
        report.invalidColumns.push(`${label} (duplicate)`);
      } else {
        seen.add(field.name);
        fields.push({ ...field, index });
        report.validColumns.push(label);
      }
    });
    report.skippedColumns = [...new Set(['DataId', 'SortOrder', 'IsEnabled', ...this.columns.map((column) => column.Name)])]
      .filter((name) => !seen.has(name));
    const idIndex = fields.find((field) => field.name === 'DataId')?.index;
    const reservedIds = new Set(this.currentData.map((row) => row.DataId));
    if (idIndex !== undefined) {
      for (const record of records) {
        const text = record[idIndex]?.trim();
        const id = Number(text);
        if (text && Number.isSafeInteger(id) && id > 0) reservedIds.add(id);
      }
    }
    let nextId = Math.max(0, ...this.currentData.map((row) => row.DataId)) + 1;
    let nextOrder = Math.max(0, ...this.currentData.map((row) => row.SortOrder)) + 1;
    for (const [recordIndex, record] of records.entries()) {
      if (record.every((value) => !value.trim())) continue;
      report.imported++;
      const idText = idIndex === undefined ? '' : record[idIndex]?.trim() ?? '';
      let errorId = idText || `Row ${recordIndex + 2} (no ID)`;
      try {
        if (!fields.length) throw new Error('No valid columns to import.');
        let id = Number(idText);
        if (!idText) {
          while (reservedIds.has(nextId)) nextId++;
          id = nextId++;
          reservedIds.add(id);
        }
        errorId = String(id);
        if (!Number.isSafeInteger(id) || id <= 0) throw new Error('ID must be a positive integer.');
        if (record.length > headers.length) throw new Error('Row contains more cells than headers.');
        const existing = this.currentData.find((row) => row.DataId === id);
        const draft: any = existing ? structuredClone(existing) : {
          Id: 0, DataId: id, IsEnabled: true, SortOrder: nextOrder,
          FirstChangesetId: 1, LastChangesetId: 1,
        };
        if (!existing) {
          for (const column of this.columns) {
            if (!['DataId', 'IsEnabled', 'SortOrder', 'Id'].includes(column.Name)) draft[column.Name] = null;
          }
        }
        for (const field of fields) {
          const value = record[field.index] ?? '';
          if (field.name === 'DataId') continue;
          if (field.name === 'SortOrder') {
            if (!value.trim()) continue;
            const order = Number(value);
            if (!Number.isSafeInteger(order)) throw new Error('Order must be an integer.');
            draft.SortOrder = order;
          } else if (field.name === 'IsEnabled') {
            const enabled = this.parseCsvBoolean(value.trim());
            if (enabled === undefined) throw new Error('Enabled must be true/false, yes/no, or 1/0.');
            draft.IsEnabled = enabled;
          } else if (field.column) {
            const parsed = this.parseCsvColumnValue(value, field.column);
            if (!parsed.valid) throw new Error(`${field.name}: invalid ${this.getTypeString(field.column)} value "${value}".`);
            const pair = columnMapping ? this.importColumnMappings.find((pair) => pair.sourceIndex === field.index) : undefined;
            if (parsed.value != null && pair && this.getImportMeasurementColumn(pair) && pair.unitId != null
              && field.column.UnitId != null && pair.unitId !== field.column.UnitId) {
              parsed.value = this.unitConverter.Convert(pair.unitId, field.column.UnitId, parsed.value);
              if (!Number.isFinite(parsed.value) || parsed.value === -999) {
                throw new Error(`${field.name}: unable to convert the selected units to the column units.`);
              }
            }
            draft[field.name] = parsed.value;
          }
        }
        if (!existing) {
          this.currentData.push(draft);
          nextOrder = Math.max(nextOrder, draft.SortOrder) + 1;
          report.added++;
        } else if (fields.some((field) => (existing as any)[field.name] != draft[field.name])) {
          Object.assign(existing, draft);
          report.updated++;
        } else {
          report.ignored++;
        }
      } catch (error) {
        report.failed.push({ id: errorId, error: error instanceof Error ? error.message : 'Unexpected row error.' });
      }
    }
    return report;
  }

  private parseCsv(content: string): string[][] {
    const records: string[][] = [];
    let record: string[] = [];
    let value = '';
    let insideQuotes = false;

    for (let index = 0; index < content.length; index++) {
      const character = content[index];
      if (character === '"') {
        if (insideQuotes && content[index + 1] === '"') {
          value += '"';
          index++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (character === ',' && !insideQuotes) {
        record.push(value);
        value = '';
      } else if ((character === '\n' || character === '\r') && !insideQuotes) {
        if (character === '\r' && content[index + 1] === '\n') index++;
        record.push(value);
        records.push(record);
        record = [];
        value = '';
      } else {
        value += character;
      }
    }

    if (value.length || record.length) {
      record.push(value);
      records.push(record);
    }
    return records;
  }

  private parseCsvBoolean(value: string): boolean | undefined {
    switch (value.toLowerCase()) {
      case 'true':
      case '1':
      case 'yes':
      case 'enabled':
        return true;
      case 'false':
      case '0':
      case 'no':
      case 'disabled':
        return false;
      default:
        return undefined;
    }
  }

  private parseCsvColumnValue(value: string, column: Column): { valid: boolean; value: any } {
    const trimmed = value.trim();
    if (column.TypeId === ColumnType.String) return { valid: true, value };
    if (!trimmed) return { valid: true, value: null };

    switch (column.TypeId as ColumnType) {
      case ColumnType.Enum: {
        const option = this.getColumnEnumValues(column).find(
          (item) => item.Name.toLowerCase() === trimmed.toLowerCase()
            || item.Label.toLowerCase() === trimmed.toLowerCase(),
        );
        return option ? { valid: true, value: option.CodeId } : { valid: false, value: null };
      }
      case ColumnType.Boolean: {
        const booleanValue = this.parseCsvBoolean(trimmed);
        return booleanValue === undefined
          ? { valid: false, value: null }
          : { valid: true, value: booleanValue };
      }
      case ColumnType.Int: {
        const number = Number(trimmed);
        return Number.isInteger(number)
          ? { valid: true, value: number }
          : { valid: false, value: null };
      }
      case ColumnType.Double:
      case ColumnType.Decimal: {
        const number = Number(trimmed);
        return Number.isFinite(number)
          ? { valid: true, value: number }
          : { valid: false, value: null };
      }
      case ColumnType.DateTime: {
        const date = new Date(trimmed);
        return Number.isNaN(date.getTime())
          ? { valid: false, value: null }
          : { valid: true, value: date.toISOString() };
      }
      default:
        return { valid: true, value };
    }
  }

  get activeFilterChips(): EngineeringFilterChip[] {
    const chips: EngineeringFilterChip[] = [];
    if (this.enabledFilter !== 'all') {
      chips.push({
        key: '__enabled',
        text: `Enabled = ${this.enabledFilter === 'enabled' ? 'Yes' : 'No'}`,
      });
    }
    this.filterRules.filter((filter) => this.isFilterComplete(filter)).forEach((filter) => {
      const column = this.getColumnByName(filter.columnName);
      if (!column) return;
      const value = this.filterValueLabel(column, filter.value);
      const value2 = this.filterValueLabel(column, filter.value2);
      chips.push({
        key: `rule-${filter.id}`,
        text: `${this.getDisplayHeading(column)} ${filter.operator} ${filter.operator === EngineeringFilterOperator.Between ? `${value} and ${value2}` : value}`,
      });
    });
    return chips;
  }

  get activeSortChips(): EngineeringFilterChip[] {
    return this.sortRules.map((rule) => ({
      key: `sort-${rule.id}`,
      text: `${this.sortColumnOptions.find((option) => option.key === rule.columnKey)?.label ?? rule.columnKey} ${rule.direction.toUpperCase()}`,
    }));
  }

  addSortRule() {
    const firstOption = this.sortColumnOptions[0];
    if (!firstOption) return;
    this.sortRules = [...this.sortRules, {
      id: this.nextSortId++,
      columnKey: firstOption.key,
      direction: 'asc',
    }];
    this.pageNumber = 1;
  }

  removeSortRule(id: number) {
    this.sortRules = this.sortRules.filter((rule) => rule.id !== id);
    this.pageNumber = 1;
  }

  removeSortChip(key: string) {
    const id = Number(key.replace('sort-', ''));
    this.removeSortRule(id);
  }

  trackSortRule(index: number, rule: EngineeringDataSortRule): number {
    return rule.id;
  }

  sortRulesChanged() {
    this.pageNumber = 1;
  }

  removeFilterChip(key: string) {
    if (key === '__enabled') {
      this.enabledFilter = 'all';
      this.pageNumber = 1;
      return;
    }
    const filterId = Number(key.replace('rule-', ''));
    this.filterRules = this.filterRules.filter((filter) => filter.id !== filterId);
    this.pageNumber = 1;
  }

  addFilter() {
    const firstColumn = this.columns[0];
    if (!firstColumn) return;
    this.filterRules = [
      ...this.filterRules,
      {
        id: this.nextFilterId++,
        columnName: firstColumn.Name,
        operator: this.operatorsForColumn(firstColumn)[0],
        value: null,
        value2: null,
      },
    ];
  }

  removeFilter(id: number) {
    this.filterRules = this.filterRules.filter((filter) => filter.id !== id);
    this.pageNumber = 1;
  }

  onFilterColumnChanged(filter: EngineeringDataFilter) {
    const column = this.getColumnByName(filter.columnName);
    if (!column) return;
    filter.operator = this.operatorsForColumn(column)[0];
    filter.value = null;
    filter.value2 = null;
    this.pageNumber = 1;
  }

  operatorsForColumn(column: Column): EngineeringFilterOperator[] {
    const basic = [EngineeringFilterOperator.Equal, EngineeringFilterOperator.NotEqual];
    if (column.TypeId === ColumnType.String) {
      return [...basic, EngineeringFilterOperator.Contains, EngineeringFilterOperator.StartsWith, EngineeringFilterOperator.EndsWith];
    }
    if (column.TypeId === ColumnType.Int || column.TypeId === ColumnType.Double) {
      return [
        ...basic,
        EngineeringFilterOperator.GreaterThanOrEqual,
        EngineeringFilterOperator.GreaterThan,
        EngineeringFilterOperator.LessThanOrEqual,
        EngineeringFilterOperator.LessThan,
        EngineeringFilterOperator.Between,
      ];
    }
    return basic;
  }

  isStringColumn(column: Column): boolean {
    return column.TypeId === ColumnType.String;
  }

  isNumericColumn(column: Column): boolean {
    return column.TypeId === ColumnType.Int
      || column.TypeId === ColumnType.Double
      || column.TypeId === ColumnType.Decimal;
  }

  isBetween(filter: EngineeringDataFilter): boolean {
    return filter.operator === EngineeringFilterOperator.Between;
  }

  trackFilter(index: number, filter: EngineeringDataFilter): number {
    return filter.id;
  }

  trackFilterChip(index: number, chip: EngineeringFilterChip): string {
    return chip.key;
  }

  get filterColumns(): Column[] {
    return [this.idFilterColumn, ...this.columns.filter((column) => column.Name !== 'DataId')];
  }

  getColumnByName(name: string): Column | undefined {
    if (name === 'DataId') return this.idFilterColumn;
    return this.columns.find((column) => column.Name === name);
  }

  private isFilterComplete(filter: EngineeringDataFilter): boolean {
    const hasValue = filter.value !== null && filter.value !== undefined && filter.value !== '';
    if (!hasValue) return false;
    return !this.isBetween(filter)
      || (filter.value2 !== null && filter.value2 !== undefined && filter.value2 !== '');
  }

  private filterValueLabel(column: Column, value: any): string {
    if (value === null || value === undefined) return '';
    if (column.TypeId === ColumnType.Enum) {
      return this.getColumnEnumValues(column).find((option) => option.CodeId === Number(value))?.Label ?? String(value);
    }
    if (column.TypeId === ColumnType.Boolean) return value ? 'Yes' : 'No';
    return String(value);
  }

  private matchesFilter(row: IData, filter: EngineeringDataFilter): boolean {
    const column = this.getColumnByName(filter.columnName);
    if (!column) return true;
    const rowValue = (row as any)[column.Name];
    if (rowValue === null || rowValue === undefined) return false;

    if (column.TypeId === ColumnType.String) {
      const value = String(rowValue).toLowerCase();
      const filterValue = String(filter.value).toLowerCase();
      switch (filter.operator) {
        case EngineeringFilterOperator.Equal: return value === filterValue;
        case EngineeringFilterOperator.NotEqual: return value !== filterValue;
        case EngineeringFilterOperator.Contains: return value.includes(filterValue);
        case EngineeringFilterOperator.StartsWith: return value.startsWith(filterValue);
        case EngineeringFilterOperator.EndsWith: return value.endsWith(filterValue);
        default: return true;
      }
    }

    if (column.TypeId === ColumnType.Enum || column.TypeId === ColumnType.Boolean) {
      const value = column.TypeId === ColumnType.Enum ? Number(rowValue) : Boolean(rowValue);
      const filterValue = column.TypeId === ColumnType.Enum ? Number(filter.value) : filter.value;
      return filter.operator === EngineeringFilterOperator.Equal
        ? value === filterValue
        : value !== filterValue;
    }

    if (column.TypeId === ColumnType.DateTime) {
      const value = this.formatDateTimeInput(rowValue).slice(0, 10);
      return filter.operator === EngineeringFilterOperator.Equal
        ? value === filter.value
        : value !== filter.value;
    }

    const value = Number(rowValue);
    const filterValue = Number(filter.value);
    switch (filter.operator) {
      case EngineeringFilterOperator.Equal: return value === filterValue;
      case EngineeringFilterOperator.NotEqual: return value !== filterValue;
      case EngineeringFilterOperator.GreaterThanOrEqual: return value >= filterValue;
      case EngineeringFilterOperator.GreaterThan: return value > filterValue;
      case EngineeringFilterOperator.LessThanOrEqual: return value <= filterValue;
      case EngineeringFilterOperator.LessThan: return value < filterValue;
      case EngineeringFilterOperator.Between: return value >= filterValue && value <= Number(filter.value2);
      default: return true;
    }
  }

  onChangesetChange(changeset: ChangesetView) {
    this.compareChangeset = changeset;
    this.comparableChangesets = this.changesets.filter(cs => cs.TableChangesetId <= changeset.TableChangesetId);
    this.loadTableDataFromChangeset(this.currentChangeset, false);
  }

  onCompareChange(changeset: ChangesetView) {
    this.loadTableDataFromChangeset(this.compareChangeset, true);
  }

  initializeData() {
    this.api.EngineeringData.GetEnums()
      .subscribe(x => {
        this.enums = x;
        this.enumValuesLoaded = true;
        this.refreshEnumTypes();
        if (this.selectedEnumType) this.onEnumTypeChange(this.selectedEnumType);
      });
    this.api.EngineeringData.GetEngineeringEnumTypes()
      .subscribe({
        next: (types) => {
          this.apiEnumTypes = [...new Set([...this.apiEnumTypes, ...types])];
          this.refreshEnumTypes();
        },
        error: () => {
          this.refreshEnumTypes();
        },
      });
    this.api.EngineeringData.GetTableList()
      .subscribe(x => {
        this.tables = [...x].sort((left, right) =>
          this.variableToDisplay(left.Name).localeCompare(
            this.variableToDisplay(right.Name),
            undefined,
            { numeric: true, sensitivity: 'base' },
          ),
        );
        this.table = this.tables[0];
        this.selectedTable = this.table;
        if (this.tables.length) this.onTableEditorTableChange(this.tables[0]);
        this.loadTableData();
        if (this.mode === 'enums') void this.loadEnumColumns();
      });
  }

  loadTableData() {
    this.originalData = [];
    this.currentData = [];
    this.columns = [];
    this.changesets = [];
    this.api.EngineeringData.GetTableColumns(this.table.Id)
      .subscribe(cols => {
        this.columns = cols;
        this.selectAllColumns();
      });
    this.api.EngineeringData.GetChangesets(this.table.Id)
      .subscribe(x => {
        this.changesets = this.comparableChangesets = x;
        this.currentChangeset = this.changesets[this.changesets.length - 1];
        this.compareChangeset = this.currentChangeset;
        this.loadTableDataFromChangeset(this.currentChangeset, false);
      });
  }

  loadTableDataFromChangeset(changeset: ChangesetView, isCompare: boolean) {
    this.api.EngineeringData.GetTableData(this.table.Id, changeset.TableChangesetId)
      .subscribe(x => {
        this.originalData = x;
        if (!isCompare) {
          this.currentData = structuredClone(this.originalData);
        }
      });
  }

  editRow: any;
  editColumn: Column;
  editValue: any;
  editEnumValues: Enum[];

  onCellClick(row: any, column: Column) {
    this.editRow = row;
    this.editColumn = column;
    this.editValue = row[column.Name];
    this.editEnumValues = this.getColumnEnumValues(column);
  }

  getColumnEnumValues(column: Column): Enum[] {
    return this.enumValuesByType.get(column.EnumType) ?? this.emptyEnumValues;
  }

  trackDataRow(index: number, row: IData): number {
    return row.DataId;
  }

  trackDataColumn(index: number, column: Column): string {
    return column.Name;
  }

  showsPreviousCellValue(column: Column): boolean {
    return column.TypeId === ColumnType.String
      || column.TypeId === ColumnType.Enum
      || column.TypeId === ColumnType.Int
      || column.TypeId === ColumnType.Double
      || column.TypeId === ColumnType.Decimal;
  }

  getOriginalDisplayValue(row: IData, column: Column): string {
    const originalRow = this.originalByDataId.get(row.DataId);
    return originalRow ? this.getDisplayValue(originalRow, column) : '';
  }

  isEditingCell(row: any, column: Column): boolean {
    return this.editRow === row && this.editColumn === column;
  }

  isVisible(row: any, column: Column): boolean {
    return this.showDisabled || row.IsEnabled;
  }

  cellChanged() {
    this.editRow[this.editColumn.Name] = this.editValue;
  }

  formatDateTimeInput(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  dateTimeChanged(value: string) {
    this.editValue = value ? new Date(value).toISOString() : null;
    this.cellChanged();
  }

  isDataModified(row: any, columnName: string): boolean {
    const originalRow = this.originalByDataId.get(row.DataId);
    return originalRow !== undefined && (originalRow as any)[columnName] != row[columnName];
  }

  isDataNew(row: any): boolean {
    return !this.originalByDataId.has(row.DataId);
  }

  onDragStart(event: DragEvent, row: any) {
    event.dataTransfer?.setData('text/plain', JSON.stringify(row));
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent, row: any) {
    event.preventDefault();
    const draggedRow = JSON.parse(event.dataTransfer?.getData('text/plain') || '{}');
    this.moveBefore(draggedRow, row);
  }

  moveBefore(newRow: any, referenceRow: any) {
    const index = this.currentData.findIndex(r => r.DataId === referenceRow.DataId);
    if (index !== -1) {
      const draggedRowIndex = this.currentData.findIndex(r => r.DataId === newRow.DataId);
      if (draggedRowIndex !== -1) {
        this.currentData.splice(draggedRowIndex, 1);
      }
      this.currentData.splice(index, 0, newRow);
      this.currentData.forEach((row, i) => row.SortOrder = i + 1);
    }
  }

  addNewRow() {
    let newRow: any = { Id: 0, DataId: 0, IsEnabled: true, FirstChangesetId: 1, LastChangesetId: 1 };
    newRow.DataId = this.currentData.length > 0 ? Math.max(...this.currentData.map(r => r.DataId)) + 1 : 1;
    newRow.SortOrder = newRow.DataId;
    this.columns.forEach(col => {
      newRow[col.Name] = null;
    });
    this.currentData.push(newRow);
    this.pageNumber = this.pageCount;
    requestAnimationFrame(() => {
      if (this.tableScrollElement) {
        this.tableScrollElement.scrollTop = this.tableScrollElement.scrollHeight;
      }
    });
  }

  deleteRow(row: any) {
    if (confirm('Are you sure you want to delete this row?')) {
      const index = this.currentData.findIndex(r => r.DataId === row.DataId);
      if (index !== -1) {
        this.currentData.splice(index, 1);
        this.currentData.forEach((row, i) => row.SortOrder = i + 1);
      }
    }
  }

  pasteAndFillFromClipboard() {
    navigator.clipboard.readText().then(text => {
      const rows = text.split('\n').map(row => row.split('\t'));
      // Assuming the first row is the header
      const headers = rows[0];
      for (let i = 1; i < rows.length; i++) {
        const newRow: any = {};
        rows[i].forEach((cell: string, index: number) => {
          newRow[headers[index]] = cell;
        });
        this.currentData.push(newRow);
      }
    });
  }

  discardChanges() {
    if (!window.confirm(`Discard ${this.pendingRowCount} unsaved row changes?`)) return;
    this.currentData = structuredClone(this.originalData);
    this.editRow = undefined;
    this.editValue = undefined;
    this.showOnlyChanges = false;
    this.pageNumber = 1;
    this.csvMessage = '';
  }

  saveChanges() {
    let modifiedData: IData[] = [];
    const newData = this.currentData.filter(row => this.isDataNew(row));
    const standardModifiedData = this.currentData.filter(row =>
      this.isDataModified(row, "IsEnabled") ||
      this.isDataModified(row, "SortOrder"));
    modifiedData = [...newData, ...standardModifiedData];
    this.columns.forEach(column => {
      const columnModifiedData = this.currentData.filter(row => this.isDataModified(row, column.Name));
      columnModifiedData.forEach(element => {
        if (!modifiedData.includes(element)) {
          modifiedData.push(element);
        }
      });
    });

    if (modifiedData.length > 0) {
      var unmodifiedData = this.currentData.filter(row => !modifiedData.includes(row));
      var unmodifiedIds = unmodifiedData.map(row => row.Id);
      if (confirm("Are you sure you want to create a new Changeset with " + unmodifiedData.length + " modified rows?")) {
        this.api.EngineeringData.UpdateEngineeringData(this.table.Id, modifiedData, unmodifiedIds)
          .subscribe(() => {
            this.loadTableData();
          });
      }
    }
    else {
      alert('No changes to save.');
    }
  }

  getDisplayHeading(column: Column): string {
    if (column === this.idFilterColumn) return 'ID';
    let displayName = column.Name;
    if (<ColumnType>column.TypeId == ColumnType.Enum)
      displayName = displayName.slice(0, displayName.length - 2);
    return this.variableToDisplay(displayName);
  }

  variableToDisplay(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  }

  getTypeString(column: Column): string {
    return ColumnType[column.TypeId];
  }

  getContextString(column: Column): string {
    return column.ContextId ? MeasurementContextDescriptions.get(column.ContextId) ?? '' : '';
  }

  getUnitString(column: Column): string {
    return column.UnitId != null ? UnitTypeDescriptions.get(column.UnitId) ?? '' : '';
  }

  getAlternateUnit(column: Column): UnitType | undefined {
    if (!this.showAlternateUnits || column.ContextId == null || column.UnitId == null
      || (column.TypeId !== ColumnType.Double && column.TypeId !== ColumnType.Decimal)) return undefined;
    const usUnit = UsUnits.get(column.ContextId);
    const siUnit = SiUnits.get(column.ContextId);
    if (usUnit === undefined || siUnit === undefined || usUnit === siUnit) return undefined;
    if (column.UnitId === usUnit) return siUnit;
    if (column.UnitId === siUnit) return usUnit;
    return undefined;
  }

  getColumnUnitHeading(column: Column): string {
    if (!column.UnitId && !(column.TypeId === ColumnType.Double
      && column.ContextId === MeasurementContext.Temperature && column.UnitId === UnitType.F)) return '';
    const unit = this.getUnitString(column);
    if (!unit) return '';
    const alternateUnit = this.getAlternateUnit(column);
    return alternateUnit === undefined
      ? `(${unit})`
      : `(${unit}) / (${UnitTypeDescriptions.get(alternateUnit)})`;
  }

  getDisplayValue(row: any, column: Column): string {
    const value = (<any>row)[column.Name];
    
    switch (<ColumnType>column.TypeId) {
      case ColumnType.String:
      case ColumnType.Boolean:
      case ColumnType.DateTime:
        return value !== undefined && value !== null ? value.toString() : '';
      case ColumnType.Int:
        return value !== undefined && value !== null ? value.toString() : '';
      case ColumnType.Double:
      case ColumnType.Decimal: {
        if (value === undefined || value === null) return '';
        const formatted = Number(value).toFixed(column.DecimalDigits);
        const alternateUnit = this.getAlternateUnit(column);
        if (alternateUnit === undefined) return formatted;
        const converted = this.unitConverter.Convert(column.UnitId, alternateUnit, Number(value));
        return Number.isFinite(converted) && converted !== -999
          ? `${formatted} / ${converted.toFixed(column.DecimalDigits)}`
          : formatted;
      }
      case ColumnType.Enum:
        return this.enumValuesByCode.get(column.EnumType)?.get(value)?.Label || '';
      default:
        return value !== undefined && value !== null ? value.toString() : '';
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const utcDateString = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateString);
    if (date.getFullYear() < 1980 || !date.valueOf()) return 'N/A';
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }
}
